import { Icon } from '../../atoms/Icon/Icon';
import { Text } from '../../atoms/Text/Text';
import { HelpIcon } from '../HelpIcon/HelpIcon';
import styles from './TableHeaderLabel.module.css';

/**
 * Figma: `_Table header label` (323:6364) — the column name inside a table
 * header cell, with an optional sort arrow and an optional help tooltip.
 *
 * The set advertises 16 variants across `Help icon` x `Arrow` x `State`, but
 * `State` is inert: every Default/Hover pair is pixel-identical — same size,
 * same gap, same `text/primary` label, same icon fills. The annotation says
 * hover is where the tooltip appears, and the tooltip is not drawn in any of
 * these variants; it lives in `Help icon`, which owns its own open state. So
 * `State` does not become a prop and there is nothing to force. See plan.md §7.
 *
 * `Arrow` is the sort indicator, which is really the column's sort *state*, so
 * it is one enum rather than a boolean plus a direction.
 *
 * **Since 2026-09-21 the glyph no longer shows the direction.** Figma's `Arrow`
 * went from `Down | Up` to `False | Chevron selector`: the up and down arrows
 * were removed and every sortable column draws the same filled `expand fill`
 * selector. `asc`/`desc` stay in the API because the header cell turns them
 * into `aria-sort`, which is now the only place the direction is exposed (the
 * user's call, over keeping a chevron on the sorted column). The annotation on
 * the set still says "Down/Up for active sort" — stale, flagged.
 *
 * The `_` prefix marks it internal in Figma — compose it through
 * `TableHeaderCell` rather than exporting it.
 */
export type TableHeaderSort = 'none' | 'selector' | 'asc' | 'desc';

export type TableHeaderLabelProps = {
  children: string;
  /**
   * Figma `Arrow`. `none` is a column that does not sort; `selector` is
   * sortable but unsorted; `asc`/`desc` are the active directions.
   */
  sort?: TableHeaderSort;
  /** Figma `Help icon=True` — the tooltip body. Omit for no help icon. */
  helpText?: string;
  /** Second line in the help tooltip. */
  helpSupportingText?: string;
  className?: string;
};

/**
 * The help trigger's glyph. `Help icon` defaults to `globe`, but every
 * `_Table header label` swaps its instance to `info` (412:46683), the circled "i".
 */
const HELP_ICON = <Icon name="info" />;

/** Figma `Arrow=Chevron selector` — the filled `expand fill`, whatever the state. */
const SORT_ICON = 'expandFill';

/** What a screen reader should hear instead of a bare glyph. Not in Figma. */
const sortLabel = {
  selector: 'Sortable',
  asc: 'Sorted ascending',
  desc: 'Sorted descending',
} as const;

export function TableHeaderLabel({
  children,
  sort = 'none',
  helpText,
  helpSupportingText,
  className,
}: TableHeaderLabelProps) {
  return (
    <span className={[styles.root, className].filter(Boolean).join(' ')}>
      {/*
       * Figma: `Text sm/leading-none/Medium`, text/primary — 12/12.
       *
       * The standalone `_Table header label` component is 54x16 and its text
       * carries no bound style at all (an unbound 12/16). Every instance of it,
       * including the one inside `Table header cell`, overrides that with
       * `Text sm/leading-none/Medium` and measures 12 tall. The bound style is
       * followed here: it is the one that states an intent, and it is what the
       * design's own header cells are built from. Flagged for the designer.
       *
       * Read the component's unbound box instead of the instances and you get
       * 16, which is how the Table parity suite briefly came to assert a 40px
       * header row. The instances are the design.
       */}
      <Text size="sm" leading="none" weight="medium" color="primary" className={styles.label}>
        {children}
      </Text>

      {helpText !== undefined && (
        <HelpIcon text={helpText} supportingText={helpSupportingText} icon={HELP_ICON} />
      )}

      {sort !== 'none' && (
        /*
         * Figma draws the glyph at 12x12, which is also its native size.
         * Decorative on its own — the cell carries `aria-sort` — but the title
         * keeps the state discoverable on hover.
         */
        <Icon name={SORT_ICON} className={styles.sortIcon} label={sortLabel[sort]} />
      )}
    </span>
  );
}
